/* eslint-disable max-len */
import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { Button, Card, Grid } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ObjectSchema } from 'yup';
import { useReactToPrint } from 'react-to-print';
import { useParams } from 'react-router-dom';
import ComponentPageHeader from '../../../Common/PageHeader/ComponentPageHeader';
import DataTable from '../../../Common/DataTable';
import style from '../utils/reportingStyle.module.scss';
import { GeneratePDFListingInterface, TypesPDFValues } from '../utils/types';
import { setLoader } from '../../../redux/loaderSlice';
import { RootState } from '../../../redux/rootState';
import ComponentTextInput from '../../../Common/ComponentTextinput/ComponentTextInput';
// import ComponentSelectInput from '../../../Common/ComponentSelectInput';
import { DefaultPDFValues, GenerateForms, PDFFormSchema } from '../utils/helper';
import { getListContentsListStart, getSignatureStart, setSelectedSignature } from '../redux/sliceReportWorkspace';
import ScreenReportPdf from '../../ScreenReportPdf';
import { GetReportPdfPayload } from '../../ScreenReportPdf/types';
import {
  getReportPdf1Start, getReportPdf2aStart, getReportPdf2bStart, getReportPdf2cStart, getReportPdf3Start, getReportPdf4Start,
  resetPdfData,
  setCheckedSections,
} from '../../ScreenReportPdf/redux/reportPdfSlice';
import { DocumentParts } from '../../ScreenReportPdf/constant';
import ComponentSelectInput from '../../../Common/ComponentSelectInput';
import { reportStyleEntries, reportStyleObj } from '../utils/constants';
import RequiredFiled from '../../../utils/RequiredField';
import { showErrorToaster } from '../../../Common/ComponentToast/ComponentSuccessToasts';

function ComponentGeneratePDF() {
  const dispatch = useDispatch();
  const {
    control, handleSubmit, formState: { errors }, setValue, watch,
  } = useForm<TypesPDFValues>({
    resolver: yupResolver(PDFFormSchema as ObjectSchema<TypesPDFValues>),
    defaultValues: DefaultPDFValues,
  });
  const [data, setData] = useState<GeneratePDFListingInterface[]>([]);
  const [signatureError, setSignatureError] = useState(false);
  const [pdfSections, setPdfSections] = useState<string[]>([]);
  const { allListContents, isLoading, signatureData } = useSelector((state:RootState) => state.reportWorkspaceReducer);
  const {
    reportPdfResponse1, reportPdfResponse2a, reportPdfResponse2b, reportPdfResponse2c, reportPdfResponse3, reportPdfResponse4,
    isPdfError1, isPdfError2a, isPdfError2b, isPdfError2c, isPdfError3, isPdfError4,
  } = useSelector((state:RootState) => state.reportPdfSliceReducer);
  const printRef = useRef(null);
  const { jobOrderId } = useParams();
  const sigValue = watch(GenerateForms.signature);

  const handleUnmountPdf = () => {
    dispatch(setLoader(false));
    setPdfSections([]);
    dispatch(resetPdfData());
  };

  const handlePrint = useReactToPrint({
    content: () => printRef?.current,
    removeAfterPrint: true,
    copyStyles: true,
    onAfterPrint() {
      handleUnmountPdf();
    },
    documentTitle: `${reportPdfResponse1?.JobOrderData?.SiteName?.replace(/[\r\n]+/g, ' ')?.trim() || ''}_${reportPdfResponse1?.JobOrderData?.SerialNumber || ''}` || '',
  });

  const columns = useMemo(
    () => [
      { key: 'sn', label: '' },
      { key: 'report', label: 'Report' },
    ],
    [],
  );

  const handleData = () => {
    dispatch(getListContentsListStart(false));
  };

  useEffect(() => {
    handleData();
    setValue(GenerateForms.jobOrder, String(jobOrderId));
    dispatch(getSignatureStart());
  }, []);

  useEffect(() => {
    dispatch(setLoader(isLoading));
  }, [isLoading]);

  useEffect(() => {
    if (allListContents?.length > 0) {
      const transformedData = allListContents?.map((ele, index) => ({
        sn: index,
        report: ele.Title,
        id: String(ele.PartNumber),
        isChecked: false,
      }));
      transformedData[0].isChecked = true;
      setData(transformedData);
    }
  }, [allListContents]);

  const handleCheckBoxWithKey = (id: string) => {
    const updatedData = [...data];
    const effId = updatedData.findIndex((ele:GeneratePDFListingInterface) => ele.id === id);

    if (effId > -1) {
      updatedData[effId].isChecked = !updatedData[effId].isChecked;
    }

    if (id === '1') {
      updatedData[1].isChecked = !updatedData[effId].isChecked;
    }

    if (id === '2') {
      updatedData[0].isChecked = !updatedData[effId].isChecked;
    }

    setData([...updatedData]);
  };

  const onSubmit = (_values: TypesPDFValues) => {
    if (!sigValue) {
      setSignatureError(true);
    } else {
      let str1 = '';
      let str2 = '';
      let str3 = '';
      let str4 = '';

      let isTOC = false;

      data?.forEach((element) => {
        if (element?.isChecked) {
          if (Number(element?.id) >= 0 && Number(element?.id) <= Number(DocumentParts.Defect_Graph)) {
            if (Number(element?.id) === Number(DocumentParts.Table_of_Contents)) {
              isTOC = true;
              str1 += '';
            } else {
              str1 += `${element?.id},`;
            }
          } else if (Number(element?.id) >= Number(DocumentParts.Summary_of_Inspection) && Number(element?.id) <= Number(DocumentParts.Data_Sheets)) {
            str2 += `${element?.id},`;
          } else if (Number(element?.id) >= Number(DocumentParts.Tube_Charts) && Number(element?.id) < Number(DocumentParts.Phase_Charts)) {
            str3 += `${element?.id},`;
          } else {
            str4 += `${element?.id},`;
          }
        }
      });

      const finalString = (str1 + str2 + str3 + str4 + (isTOC ? DocumentParts.Table_of_Contents : ''));

      dispatch(setCheckedSections(finalString?.split(',')));

      if (str1.length > 0) {
        const payload:GetReportPdfPayload = {
          jobOrderId: String(jobOrderId),
          Sections: str1.slice(0, -1),
        };
        dispatch(getReportPdf1Start(payload));
        pdfSections.push('1');
      }

      // if (str2.length > 0) {
      //   const payload2:GetReportPdfPayload = {
      //     jobOrderId: String(jobOrderId),
      //     Sections: str2.slice(0, -1),
      //   };
      //   dispatch(getReportPdf2Start(payload2));
      //   pdfSections.push('2');
      // }
      if (str2.includes('6')) {
        const payload2:GetReportPdfPayload = {
          jobOrderId: String(jobOrderId),
          Sections: '6',
        };
        dispatch(getReportPdf2aStart(payload2));
        pdfSections.push('2a');
      }
      if (str2.includes('7')) {
        const payload2:GetReportPdfPayload = {
          jobOrderId: String(jobOrderId),
          Sections: '7',
        };
        dispatch(getReportPdf2bStart(payload2));
        pdfSections.push('2b');
      }
      if (str2.includes('8')) {
        const payload2:GetReportPdfPayload = {
          jobOrderId: String(jobOrderId),
          Sections: '8',
        };
        dispatch(getReportPdf2cStart(payload2));
        pdfSections.push('2c');
      }

      if (str3.length > 0) {
        const payload3:GetReportPdfPayload = {
          jobOrderId: String(jobOrderId),
          Sections: str3.slice(0, -1),
        };
        dispatch(getReportPdf3Start(payload3));
        pdfSections.push('3');
      }

      if (str4.length > 0) {
        const payload4:GetReportPdfPayload = {
          jobOrderId: String(jobOrderId),
          Sections: str4.slice(0, -1),
        };
        dispatch(getReportPdf4Start(payload4));
        pdfSections.push('4');
      }

      dispatch(setLoader(true));
      // setTimeout(() => {
      //   handlePrint();
      //   // dispatch(setLoader(false));
      // }, 8000);
    }
  };

  useEffect(() => {
    let isResponseAvailable = false;
    let pdfErr = false;

    if (pdfSections.includes('1')) {
      if (isPdfError1 > 0) {
        pdfErr = true;
      } else if (Object.keys(reportPdfResponse1)?.length > 0) {
        isResponseAvailable = true;
      } else {
        isResponseAvailable = false;
        pdfErr = false;
        return;
      }
    }
    if (pdfSections.includes('2a')) {
      if (isPdfError2a > 0) {
        pdfErr = true;
      } else if (Object.keys(reportPdfResponse2a)?.length > 0) {
        isResponseAvailable = true;
      } else {
        isResponseAvailable = false;
        pdfErr = false;
        return;
      }
    }
    if (pdfSections.includes('2b')) {
      if (isPdfError2b > 0) {
        pdfErr = true;
      } else if (Object.keys(reportPdfResponse2b)?.length > 0) {
        isResponseAvailable = true;
      } else {
        isResponseAvailable = false;
        pdfErr = false;
        return;
      }
    }
    if (pdfSections.includes('2c')) {
      if (isPdfError2c > 0) {
        pdfErr = true;
      } else if (Object.keys(reportPdfResponse2c)?.length > 0) {
        isResponseAvailable = true;
      } else {
        isResponseAvailable = false;
        pdfErr = false;
        return;
      }
    }
    if (pdfSections.includes('3')) {
      if (isPdfError3 > 0) {
        pdfErr = true;
      } else if (Object.keys(reportPdfResponse3)?.length > 0) {
        isResponseAvailable = true;
      } else {
        isResponseAvailable = false;
        pdfErr = false;
        return;
      }
    }
    if (pdfSections.includes('4')) {
      if (isPdfError4 > 0) {
        pdfErr = true;
      } else if (Object.keys(reportPdfResponse4)?.length > 0) {
        isResponseAvailable = true;
      } else {
        isResponseAvailable = false;
        pdfErr = false;
        return;
      }
    }
    if (pdfSections.length === 0) {
      isResponseAvailable = false;
      pdfErr = false;
    }

    if (isResponseAvailable && pdfSections?.length > 0) {
      setTimeout(() => {
        handlePrint();
        if (pdfErr) {
          // eslint-disable-next-line no-alert
          alert('Some part in the report might be missing, kindly verify the report.');
        }
      }, 8000);
    }

    if (pdfErr && !isResponseAvailable) {
      showErrorToaster('Sorry! The report for this job cannot be generated.');
      handleUnmountPdf();
    }
  }, [reportPdfResponse1, reportPdfResponse2a, reportPdfResponse2b, reportPdfResponse2c, reportPdfResponse3, reportPdfResponse4, isPdfError1, isPdfError2a, isPdfError2b, isPdfError2c, isPdfError3, isPdfError4]);

  const handleSignature = (value: string) => {
    const signatureDetail = signatureData?.find((ele) => ele.Value === value) || null;
    dispatch(setSelectedSignature(signatureDetail));
    setSignatureError(false);
  };

  const handleReportStyleChange = (value: string) => {
    const arr = reportStyleObj[Number(value) as 1 | 2 | 3];
    let temp = [...data];
    temp = temp.map((ele:GeneratePDFListingInterface) => {
      if (arr.includes((ele.id) as DocumentParts)) {
        return { ...ele, isChecked: true };
      }

      return { ...ele, isChecked: false };
    });

    setData([...temp]);
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <ComponentPageHeader subHeading="Generate PDF" />
        <div style={{ padding: '20px', height: '86vh', overflow: 'auto' }}>
          <Grid container>
            <Grid item xs={12} sm={12} md={12}>
              <div className={style.tableHeading}>
                <span className={style.tableHeadingText}>
                  PDF in Box
                </span>
              </div>
            </Grid>
            <Grid item xs={12} sm={12} md={12}>
              <Card className="customerInfo_main_container">
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                    <div className="div_label_text">
                      <label className="label_Style_Customer">Job Order #</label>
                      <ComponentTextInput
                        control={control}
                        inputProps={{ maxLength: 255 }}
                        className="nonCapitalizeField"
                        name={GenerateForms.jobOrder}
                        isDisable
                        id={GenerateForms.jobOrder}
                        size="small"
                        errors={errors}
                      />
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={12} md={5} lg={5} xl={5}>
                    <div className="div_label_text">
                      <label className="label_Style_Customer">
                        Signature
                        <RequiredFiled />
                      </label>
                      <ComponentSelectInput
                        name={GenerateForms.signature}
                        control={control}
                        errors={errors}
                        size="small"
                        entries={signatureData}
                        handleChange={handleSignature}
                      />
                      {signatureError && <span className="errorMessage">Signature is required</span>}
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                    <div className="div_label_text">
                      <label className="label_Style_Customer">Report Type</label>
                      <ComponentSelectInput
                        name={GenerateForms.reportStyle}
                        control={control}
                        errors={errors}
                        handleChange={handleReportStyleChange}
                        size="small"
                        entries={reportStyleEntries}
                      />
                    </div>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
            <Grid item xs={12} sm={12} md={12}>
              <DataTable<GeneratePDFListingInterface>
                data={data}
                columns={columns}
                totalRecords={data?.length}
                checkBoxWithKey={['sn']}
                handleCheckBoxWithKey={handleCheckBoxWithKey}
                handleData={handleData}
              />
            </Grid>
          </Grid>
          <Grid item md={12} sm={12} xs={12}>
            <div className="save_and_next_div" style={{ marginTop: '20px' }}>
              <div className="button_margin_left">
                <Button
                  type="submit"
                  className="button_save_and_next"
                >
                  Preview
                </Button>
              </div>
            </div>
          </Grid>
        </div>
      </form>
      <ScreenReportPdf printRef={printRef} />
    </>
  );
}

export default ComponentGeneratePDF;
